import {edgeFailureMessage} from './remoteProposal';
import React, { useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';

// The deployed Edge Function slug may differ from its name (Supabase sometimes
// auto-generates one, e.g. create-inspector → "bright-action"). Override via
// REACT_APP_SIGN_PROPOSAL_SLUG if needed.
const SIGN_FN_SLUG = process.env.REACT_APP_SIGN_PROPOSAL_SLUG || 'sign-proposal';

const money = (n) => '$' + (Number(n) || 0).toLocaleString();
const C = {
  bg: '#0f172a', card: '#fff', ink: '#0f172a', sub: '#64748b', line: '#e2e8f0',
  brand: '#7c3aed', ok: '#16a34a',
};

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.strokeStyle = '#0f172a';
  }, []);
  const pos = (e) => {
    const c = ref.current, r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return [(t.clientX - r.left) * (c.width / r.width), (t.clientY - r.top) * (c.height / r.height)];
  };
  const down = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const c = ref.current, ctx = c.getContext('2d'), p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current[0], last.current[1]); ctx.lineTo(p[0], p[1]); ctx.stroke();
    last.current = p;
  };
  const up = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL('image/png')); };
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); onChange(''); };
  return (
    <div>
      <canvas ref={ref} width={520} height={130}
        style={{ width: '100%', height: 130, border: `1px solid ${C.line}`, borderRadius: 10, touchAction: 'none', background: '#fbfbfd' }}
        onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
        onTouchStart={down} onTouchMove={move} onTouchEnd={up} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ fontSize: 12, color: C.sub }}>Sign above with your finger or mouse</span>
        <button onClick={clear} style={{ background: 'none', border: 'none', color: C.brand, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Clear</button>
      </div>
    </div>
  );
}

export default function SignProposalPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [state, setState] = useState('loading'); // loading | ready | signed | done | error
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [sig, setSig] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState('error'); setError('This link is missing its signing token.'); return; }
    (async () => {
      try {
        const { data: res, error: e } = await supabase.functions.invoke(SIGN_FN_SLUG, { body: { action: 'load', token } });
        if (e) { setState('error'); setError(await edgeFailureMessage(e,'We could not open this proposal. The link may have expired.')); return; }
        if (res?.error) { setState('error'); setError(res.error); return; }
        setData(res.proposal);
        setName(res.proposal?.ownerName || '');
        setState(res.alreadySigned ? 'signed' : 'ready');
      } catch { setState('error'); setError('Something went wrong opening this proposal.'); }
    })();
  }, [token]);

  async function submit() {
    if (!sig) { setError('Please add your signature before submitting.'); return; }
    if (!name.trim()) { setError('Please type your name.'); return; }
    setSubmitting(true); setError('');
    try {
      const { data: res, error: e } = await supabase.functions.invoke(SIGN_FN_SLUG, { body: { action: 'sign', token, signerName: name.trim(), signature: sig } });
      if (e || res?.error) { setError(res?.error || await edgeFailureMessage(e,'Could not submit your signature. Please try again.')); setSubmitting(false); return; }
      setState('done');
    } catch { setError('Could not submit your signature. Please try again.'); setSubmitting(false); }
  }

  const shell = (inner) => (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '24px 12px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 720 }}>{inner}</div>
    </div>
  );
  const panel = (inner, extra) => (
    <div style={{ background: C.card, borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.35)', ...extra }}>{inner}</div>
  );

  if (state === 'loading') return shell(panel(<div style={{ textAlign: 'center', color: C.sub }}>Loading proposal…</div>));
  if (state === 'error') return shell(panel(<div style={{ textAlign: 'center' }}><div style={{ fontSize: 40 }}>⚠️</div><h2 style={{ color: C.ink }}>Unable to open</h2><p style={{ color: C.sub }}>{error}</p></div>));
  if (state === 'done') return shell(panel(<div style={{ textAlign: 'center' }}><div style={{ fontSize: 44 }}>✅</div><h2 style={{ color: C.ink, margin: '8px 0' }}>Thank you — signed!</h2><p style={{ color: C.sub }}>Your authorization has been sent to {data?.company?.name || 'the company'}. You may close this page.</p></div>));

  const p = data || {};
  const addr = [p.address?.address, p.address?.city, p.address?.state, p.address?.zip].filter(Boolean).join(', ');
  const bySection = [
    { label: 'SECTION I — Active Infestation / Infection', cats: [1, 2, 3] },
    { label: 'SECTION II — Conditions', cats: [4] },
    { label: 'FURTHER INSPECTION', cats: [5] },
  ].map(s => ({ ...s, items: (p.findings || []).filter(f => s.cats.includes(f.cat)) })).filter(s => s.items.length);

  return shell(
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {panel(
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, borderBottom: `1px solid ${C.line}`, paddingBottom: 14, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: C.ink }}>{p.company?.name || 'Inspection Proposal'}</div>
              <div style={{ fontSize: 12, color: C.sub }}>{[p.company?.address, p.company?.city].filter(Boolean).join(', ')}{p.company?.license ? ` · Lic. ${p.company.license}` : ''}</div>
              {p.company?.phone && <div style={{ fontSize: 12, color: C.sub }}>{p.company.phone}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.brand }}>PROPOSAL{p.separated ? ' · Separated' : ''}</div>
              <div style={{ fontSize: 12, color: C.sub }}>Report #{p.reportNum || '—'}</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: C.ink }}><strong>Property:</strong> {addr || '—'}</div>
          {p.ownerName && <div style={{ fontSize: 13, color: C.ink, marginTop: 2 }}><strong>Owner:</strong> {p.ownerName}</div>}
        </div>
      )}

      {panel(
        <div>
          {bySection.map(sec => (
            <div key={sec.label} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: C.sub, marginBottom: 8 }}>{sec.label}</div>
              {sec.items.map((f, i) => (
                <div key={i} style={{ padding: '10px 0', borderTop: i ? `1px solid ${C.line}` : 'none', display: 'flex', gap: 12 }}>
                  <span style={{ flexShrink: 0, fontWeight: 800, fontSize: 12, color: C.brand, minWidth: 28 }}>{f.cat}{f.letter}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {f.finding && <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 4 }}>{f.finding}</div>}
                    {f.primaryRec && <div style={{ fontSize: 12.5, color: C.ink }}>{f.primaryRec}</div>}
                    {f.component&&<div style={{fontSize:12,color:C.sub,marginTop:4}}>{f.component}{f.repairAction?` · ${f.repairAction}`:''}</div>}
                    {(f.locations||[]).map((l,index)=><div key={index} style={{fontSize:12,color:C.sub,marginTop:4}}>{[l.locationLabel||`Location ${index+1}`,l.replacementMaterial==='plywood'?'Plywood / sheathing':null,l.replacementThicknessIn&&l.replacementWidthIn?`${l.replacementThicknessIn} × ${l.replacementWidthIn} in`:l.replacementThicknessIn?`${l.replacementThicknessIn} in thick`:null,l.replacementMaterial==='plywood'&&l.replacementSheetWidthFt&&l.replacementLengthFt?`${l.replacementSheetWidthFt} × ${l.replacementLengthFt} ft sheets`:l.replacementLengthFt?`${l.replacementLengthFt} ft long`:null,l.replacementQuantity?`qty ${l.replacementQuantity}`:null,l.paintRequired===true?'Painting required':l.paintRequired===false?'No painting required':null].filter(Boolean).join(' · ')}</div>)}

                    {f.hasSecondary && f.secondaryRec && <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}><em>Alt:</em> {f.secondaryRec}{f.secPrice ? ` — ${money(f.secPrice)}` : ''}</div>}
                  </div>
                  {f.cat !== 5 && f.primaryPrice > 0 && <div style={{ flexShrink: 0, fontWeight: 800, color: C.ink }}>{money(f.primaryPrice)}</div>}
                </div>
              ))}
            </div>
          ))}
          <div style={{ background: '#f1f5f9', borderRadius: 10, padding: 16, marginTop: 4 }}>
            {p.separated ? (
              <>
                <Row label="Section I Total" value={money(p.totals?.sectionI)} bold />
                <Row label="Section II Total" value={money(p.totals?.sectionII)} bold />
              </>
            ) : (
              <Row label="Total" value={money(p.totals?.grand)} bold big />
            )}
          </div>
          {(p.findings || []).some(f => f.cat === 5) && <div style={{ fontSize: 12, color: '#4338ca', marginTop: 10 }}>ℹ Further Inspection items are not included in the totals.</div>}
        </div>
      )}

      {state === 'signed' ? (
        panel(<div style={{ textAlign: 'center' }}><div style={{ fontSize: 40 }}>✅</div><h3 style={{ color: C.ink }}>This proposal has already been signed.</h3><p style={{ color: C.sub }}>No further action is needed.</p></div>)
      ) : (
        panel(
          <div>
            <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 12 }}>{p.terms}</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>Your full name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Type your name"
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 15 }} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>Signature</label>
            <SignaturePad onChange={setSig} />
            {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{error}</div>}
            <button onClick={submit} disabled={submitting}
              style={{ marginTop: 14, width: '100%', padding: '14px', border: 'none', borderRadius: 12, background: submitting ? '#a78bfa' : C.brand, color: '#fff', fontWeight: 800, fontSize: 15, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Submitting…' : '✍️ Accept & Sign Proposal'}
            </button>
            <div style={{ fontSize: 11, color: C.sub, textAlign: 'center', marginTop: 10 }}>By signing you authorize the work described above.</div>
          </div>
        )
      )}
    </div>
  );
}

function Row({ label, value, bold, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <span style={{ fontWeight: bold ? 800 : 400, fontSize: big ? 16 : 14, color: '#0f172a' }}>{label}</span>
      <span style={{ fontWeight: 800, fontSize: big ? 20 : 16, color: '#0f172a' }}>{value}</span>
    </div>
  );
}
